<template>
  <AdminLayout page-title="User Management">
    <template #header-actions>
      <button
        @click="router.push('/admin/users/new')"
        class="dashboard-primary-btn flex items-center gap-2 text-sm"
        aria-label="Create new user"
      >
        <svg
          class="w-5 h-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4"
          />
        </svg>
        <span class="hidden sm:inline">New User</span>
      </button>
    </template>

    <div class="mx-auto">
      <div
        v-if="loading"
        class="text-center py-12 text-on-surface-variant"
      >
        Loading users...
      </div>

      <div
        v-else
        class="dashboard-card bg-surface rounded-xl border border-outline-variant/30 overflow-hidden"
      >
        <div class="hidden md:block overflow-x-auto">
          <table class="w-full">
            <thead class="bg-surface-container-low">
              <tr>
                <th class="px-6 py-4 text-left text-xs font-semibold text-on-surface uppercase tracking-wider">
                  Email
                </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-on-surface uppercase tracking-wider">
                  Name
                </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-on-surface uppercase tracking-wider">
                  Status
                </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-on-surface uppercase tracking-wider">
                  Last Login
                </th>
                <th class="px-6 py-4 text-right text-xs font-semibold text-on-surface uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/30">
              <tr
                v-for="user in usersList"
                :key="user.id"
                class="hover:bg-surface-container-low transition-colors"
              >
                <td class="px-6 py-4 text-sm font-medium text-on-surface">
                  {{ user.email }}
                </td>
                <td class="px-6 py-4 text-sm text-on-surface-variant">
                  {{ user.fullName || '—' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="px-3 py-1 text-xs font-medium rounded-full"
                    :class="
                      user.isActive
                        ? 'bg-primary/10 text-primary'
                        : 'bg-surface-container-low text-on-surface-variant'
                    "
                  >
                    {{ user.isActive ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                  {{ formatDate(user.lastLogin) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div class="flex items-center justify-end gap-2">
                    <button
                      v-if="user.id === currentUserId"
                      @click="router.push(`/admin/users/${user.id}/edit`)"
                      class="text-primary hover:text-primary-container transition-colors cursor-pointer"
                      title="Edit"
                      aria-label="Edit user"
                    >
                      <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      v-if="canShowToggleButton(user)"
                      @click="handleToggleActive(user)"
                      class="transition-colors"
                      :class="[
                        user.isActive ? 'text-error hover:text-error/80' : 'text-primary hover:text-primary-container',
                        toggleButtonDisabled(user) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
                      ]"
                      :disabled="toggleButtonDisabled(user)"
                      :title="toggleButtonTitle(user)"
                      :aria-label="user.isActive ? 'Deactivate user' : 'Activate user'"
                    >
                      <svg v-if="user.isActive" class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                      <svg v-else class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="usersList.length === 0">
                <td colspan="5" class="px-6 py-12 text-center text-on-surface-variant">
                  No users found. Create your first admin user!
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="md:hidden divide-y divide-outline-variant/30">
          <div
            v-for="user in usersList"
            :key="user.id"
            class="p-4 hover:bg-surface-container-low transition-colors"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="flex-1 min-w-0">
                <h3 class="text-sm font-medium text-on-surface truncate">{{ user.email }}</h3>
                <p class="text-xs text-on-surface-variant mt-1">{{ user.fullName || 'No name' }}</p>
                <div class="flex items-center gap-3 mt-2">
                  <span
                    class="px-2 py-1 text-xs font-medium rounded-full"
                    :class="user.isActive ? 'bg-primary/10 text-primary' : 'bg-surface-container-low text-on-surface-variant'"
                  >
                    {{ user.isActive ? 'Active' : 'Inactive' }}
                  </span>
                  <span class="text-xs text-on-surface-variant">{{ formatDate(user.lastLogin) }}</span>
                </div>
              </div>
              <div class="flex items-center gap-2 flex-shrink-0">
                <button
                  v-if="user.id === currentUserId"
                  @click="router.push(`/admin/users/${user.id}/edit`)"
                  class="text-primary hover:text-primary-container transition-colors"
                  aria-label="Edit user"
                >
                  <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  v-if="canShowToggleButton(user)"
                  @click="handleToggleActive(user)"
                  :class="[
                    user.isActive ? 'text-error' : 'text-primary',
                    toggleButtonDisabled(user) ? 'opacity-40 cursor-not-allowed' : '',
                  ]"
                  :disabled="toggleButtonDisabled(user)"
                  :title="toggleButtonTitle(user)"
                  :aria-label="user.isActive ? 'Deactivate user' : 'Activate user'"
                >
                  <svg v-if="user.isActive" class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                  <svg v-else class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div v-if="usersList.length === 0" class="px-6 py-12 text-center text-on-surface-variant">
            No users found. Create your first admin user!
          </div>
        </div>
      </div>
    </div>

    <ConfirmModal
      :open="toggleModalOpen"
      :title="toggleModalTitle"
      :message="toggleModalMessage"
      :confirm-label="toggleModalConfirmLabel"
      cancel-label="Cancel"
      :variant="toggleModalVariant"
      @confirm="confirmToggle"
      @cancel="closeToggleModal"
    />
  </AdminLayout>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { authService } from "../../utils/auth.js";
import {
  listAdminUsers,
  toggleAdminUserActive,
  getDeactivateBlockReason,
} from "../../utils/users.js";
import AdminLayout from "../../components/admin/AdminLayout.vue";
import ConfirmModal from "../../components/admin/ConfirmModal.vue";
import { showAdminToast } from "../../utils/adminToast.js";

const router = useRouter();
const currentUserId = authService.getCurrentUser()?.userId || null;

const usersList = ref([]);
const loading = ref(false);
const toggleModalOpen = ref(false);
const pendingToggleUser = ref(null);

const activeAdminCount = computed(
  () => usersList.value.filter((user) => user.isActive).length
);

const isCurrentUser = (user) =>
  currentUserId != null && String(user.id) === String(currentUserId);

const getBlockReason = (user) =>
  getDeactivateBlockReason(user.id, user.isActive, {
    currentUserId,
    activeAdminCount: activeAdminCount.value,
  });

const canShowToggleButton = (user) => {
  if (!user.isActive) return true;
  return !isCurrentUser(user);
};

const toggleButtonDisabled = (user) => !!getBlockReason(user);

const toggleButtonTitle = (user) => {
  const blockReason = getBlockReason(user);
  if (blockReason) return blockReason;
  return user.isActive ? "Deactivate" : "Activate";
};

const formatDate = (dateString) => {
  if (!dateString) return "Never";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};


const loadUsers = async () => {
  loading.value = true;
  try {
    usersList.value = await listAdminUsers();
  } catch (error) {
    console.error("Error loading users:", error);
    showAdminToast(error.message || "Error loading users", "error");
  } finally {
    loading.value = false;
  }
};

const toggleModalTitle = computed(() => {
  if (!pendingToggleUser.value) return "";
  return pendingToggleUser.value.isActive ? "Deactivate user" : "Activate user";
});

const toggleModalMessage = computed(() => {
  if (!pendingToggleUser.value) return "";
  const action = pendingToggleUser.value.isActive ? "deactivate" : "activate";
  return `Are you sure you want to ${action} ${pendingToggleUser.value.email}?`;
});

const toggleModalConfirmLabel = computed(() =>
  pendingToggleUser.value?.isActive ? "Deactivate" : "Activate"
);

const toggleModalVariant = computed(() =>
  pendingToggleUser.value?.isActive ? "danger" : "default"
);

const handleToggleActive = (user) => {
  const blockReason = getBlockReason(user);
  if (blockReason) {
    showAdminToast(blockReason, "error");
    return;
  }

  pendingToggleUser.value = user;
  toggleModalOpen.value = true;
};

const closeToggleModal = () => {
  toggleModalOpen.value = false;
  pendingToggleUser.value = null;
};

const confirmToggle = async () => {
  const user = pendingToggleUser.value;
  closeToggleModal();
  if (!user) return;

  try {
    const updated = await toggleAdminUserActive(user.id);
    showAdminToast(
      `User ${updated.isActive ? "activated" : "deactivated"} successfully`,
      "success"
    );
    await loadUsers();
  } catch (error) {
    console.error("Error toggling user status:", error);
    showAdminToast(error.message || "Failed to update user status", "error");
  }
};

onMounted(async () => {
  if (!authService.isAuthenticated()) {
    router.push("/admin/login");
    return;
  }

  await loadUsers();
});
</script>

<style scoped>
.dashboard-card {
  box-shadow: 0 10px 30px rgba(21, 66, 18, 0.08);
}

.dashboard-primary-btn {
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  background-color: #154212;
  color: #a9f862;
  font-weight: 600;
  border: none;
  box-shadow: 0 4px 10px rgba(21, 66, 18, 0.25);
  transition: background-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;
}

.dashboard-primary-btn:hover {
  background-color: #2d5a27;
  box-shadow: 0 6px 16px rgba(21, 66, 18, 0.3);
  transform: translateY(-1px);
}
</style>
